import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import ctgMain from '../Images/ctg.jpeg';
import heroImage from '../Images/ctgpage.jpg';
import Footer from '../Pages/Footer';

const imageDetails = {
  [ctgMain]: {
    category: 'Cardiotocography',
    heading: 'Cardiotocography',
    title: 'MT FD 150',
    subtitle: 'Fetal Monitor — Real-Time FHR & TOCO Monitoring',
    description:
      'MT FD 150 is a high-performance fetal monitor designed for accurate and continuous maternal and fetal monitoring during pregnancy and labor. It provides real-time monitoring of FHR, TOCO, and fetal movement with intelligent analysis, clear waveform display, and reliable data management for modern obstetrical care.',
    sections: {
      "Advanced Fetal Monitoring": {
        icon: "❤️",
        items: [
          "Real-time monitoring of FHR, TOCO, and fetal movement",
          "Accurate fetal heart rate detection with 9-crystal broad beam transducer",
          "Normal FHR range indication (120 BPM - 160 BPM)",
          "Continuous monitoring support for up to 24 hours",
          "Manual fetal movement recording",
        ],
      },

      "Smart Display & Operation": {
        icon: "🖥️",
        items: [
          '8" color LCD display with foldable/convertible screen',
          "Clear monitoring curves and numeric data display",
          "Compact design for desktop or wall mounting",
          "User-friendly operation with intuitive interface",
          "Optional multi-language support",
        ],
      },

      "Intelligent Analysis": {
        icon: "📊",
        items: [
          "FHR analysis includes:",
          "Short-Term Variation (STV)",
          "Signal Loss Detection",
          "Basal Heart Rate",
          "Acceleration & Deceleration Analysis",
          "High Episode & Low Episode Monitoring",
          "Alarm indication for abnormal fetal heart rate",
          "Reliable clinical reference data support",
        ],
      },

      "Printing & Data Storage": {
        icon: "🖨️",
        items: [
          "Built-in high-resolution thermal printer",
          "Stores, plays back, and prints monitoring data for up to 12 hours",
          "Extra long-life printer mechanism",
          "Real-time waveform printing support",
          "In-built thermal printing system for reports",
        ],
      },

      "Connectivity & Networking": {
        icon: "📡",
        items: [
          "Built-in RJ45 communication port",
          "Supports connection with central monitoring system",
          "Suitable for obstetrical department network monitoring",
          "Stable and efficient data communication",
        ],
      },

      "Power & Reliability": {
        icon: "🔋",
        items: [
          "Compact and lightweight design",
          "Stable long-duration monitoring performance",
          "Suitable for hospitals, maternity centers, and clinics",
          "Reliable operation for continuous fetal assessment",
        ],
      },
    },
  },
};

const CardiotocographyDetail = () => {
  const [isMobile, setIsMobile] = useState(false);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const detail = imageDetails[ctgMain];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>Cardiotocography Machine | Fetal Monitor by Mindron Meditech</title>
        <meta
          name="description"
          content="Explore MT FD 150 cardiotocography fetal monitor by Mindron Meditech for real-time FHR, TOCO, fetal movement monitoring, intelligent analysis, printing, and obstetrical care."
        />
        <link rel="canonical" href="https://www.mindronmeditech.com/products/cardiotocography" />
      </Helmet>

      <div style={{ fontFamily: 'sans-serif', margin: 0, padding: 0, overflowX: 'hidden' }}>

        {/* HERO */}
        <div style={{
          height: isMobile ? '30vh' : '80vh',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <img
            src={heroImage}
            alt="Hero"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
          }} />
        </div>

        {/* PRODUCT INTRO */}
        <div style={{
          display: 'flex',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: '40px',
          padding: isMobile ? '32px 6%' : '48px 6%',
          alignItems: 'flex-start',
        }}>
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#e8f0fe',
              color: '#1a56db',
              fontSize: '13px',
              fontWeight: '600',
              padding: '4px 14px',
              borderRadius: '20px',
              marginBottom: '14px',
            }}>
              {detail?.category}
            </div>
            <h1 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '800',
              color: '#0a1628',
              margin: '0 0 8px',
              lineHeight: 1.2,
            }}>
              {detail?.title}
            </h1>
            <h2 style={{
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: '500',
              color: '#444',
              margin: '0 0 20px',
            }}>
              {detail?.subtitle}
            </h2>
            <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', maxWidth: '520px' }}>
              {detail?.description}
            </p>
          </div>

          {/* Right: product image */}
          <div style={{
            flex: '0 0 auto',
            width: isMobile ? '100%' : '340px',
            background: '#f4f6fb',
            borderRadius: '14px',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src={ctgMain}
              alt="CTG Device"
              style={{ width: '100%', maxHeight: '260px', objectFit: 'contain', borderRadius: '8px' }}
            />
          </div>
        </div>

        {/* KEY FEATURES GRID */}
        <div style={{ padding: isMobile ? '16px 6% 40px' : '16px 6% 56px' }}>
          <div style={{ width: '50px', height: '5px', backgroundColor: '#cc0000', borderRadius: '2px', marginBottom: '12px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0a1628', marginBottom: '28px' }}>
            Key Features
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px',
          }}>
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div key={idx} style={{ background: '#f4f6fb', borderRadius: '12px', padding: '22px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0a1628', margin: 0 }}>
                    {sectionName}
                  </h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', listStyle: 'disc' }}>
                  {sectionData.items.map((item, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#555', lineHeight: '1.8' }}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <Footer />

        <style>{`
          @media (max-width: 768px) {
            .header-container { height: 30vh !important; }
          }
          *::-webkit-scrollbar { display: none; }
          * { scrollbar-width: none; }
          body { overflow-x: hidden; }
        `}</style>
      </div>
    </>
  );
};

export default CardiotocographyDetail;